import styled from 'styled-components';

import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { DataTableSubRows } from 'common/components/cookbook/DataTable/DataTableSubRows';
import { Stack } from 'common/components/cookbook/Stack';
import { TextLink } from 'common/components/cookbook/TextLink';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { useVenuesQuery } from 'common/hooks/useVenuesQuery';
import { formatMoney } from 'common/utils/moneyUtils';
import { formatNumber } from 'common/utils/numberUtils';
import { RecommendationReasons } from 'features/selection-recommendations/common/RecommendationReasons/RecommendationReasons';
import { getListingDuration } from 'features/selection-recommendations/utils';

import { DelistingRecommendationItem } from './types';

type Props = {
  parent: DelistingRecommendationItem;
};

const Venue = styled(Stack)`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DelistingRecommendationSubList = ({ parent }: Props) => {
  const f = useFormatMessage();
  const locale = useUserLocale();
  const countryCodes = useSelectedCountryCodes();
  const { data: listMetaVenues, error } = useVenuesQuery({ countryCodes });

  if (error) {
    return (
      <ContentContainer>
        <ApiErrorNotification error={error} />
      </ContentContainer>
    );
  }

  return (
    <DataTableSubRows
      dataSource={parent.venueItems}
      columns={[
        {
          id: 'venues',
          header: '',
          width: 450,
          render: ({ record: { venueId } }) => {
            const venue = listMetaVenues?.items?.find((v) => v.id === venueId);
            return <Venue>{venue?.name}</Venue>;
          },
        },
        {
          id: 'expectedBusinessImpact',
          header: f('selection_recommendations.expected_business_impact'),
          width: 'auto',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          render: ({ record: { expectedBusinessImpact } }) => {
            return expectedBusinessImpact?.pureProfitMarginImpact?.amount !== undefined
              ? formatMoney(locale, expectedBusinessImpact.pureProfitMarginImpact)
              : '-';
          },
        },
        {
          id: 'reasons',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.reason'),
          width: 'auto',
          render: ({ record: { reasons } }) => {
            return <RecommendationReasons reasons={reasons} />;
          },
        },
        {
          id: 'salesPrice',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          render: ({ record: { salesPrice } }) => {
            return <>{formatMoney(locale, salesPrice)}</>;
          },
        },
        {
          id: 'soldUnits',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          render: ({ record: { soldUnitsPast90Days } }) => soldUnitsPast90Days,
        },
        {
          id: 'costPrice',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          render: ({ record: { costPrice } }) => {
            if (!costPrice) return <>{f('common.not_available_acronym')}</>;
            return <>{formatMoney(locale, costPrice)}</>;
          },
        },
        {
          id: 'ppm',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 100,
          render: ({ record: { pureProfitMargin } }) => {
            if (!pureProfitMargin) return '-';
            return `${formatNumber(locale, pureProfitMargin)}%`;
          },
        },
        {
          id: 'ppmGap',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          render: ({ record: { pureProfitMarginGapBps } }) => {
            const ppmGap = formatNumber(locale, pureProfitMarginGapBps);

            return pureProfitMarginGapBps && pureProfitMarginGapBps > 0
              ? `+${ppmGap}`
              : ppmGap || '-';
          },
        },
        {
          id: 'price-index',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.price_index'),
          width: 100,
          render: ({ record: { priceIndex } }) => {
            if (!priceIndex) return '-';
            return formatNumber(locale, Number(priceIndex), 2);
          },
        },
        {
          id: 'stockCount',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 120,
          render: ({ record: { stockCount } }) => {
            if (!stockCount) return '0';
            return formatNumber(locale, stockCount);
          },
        },
        {
          id: 'shrink',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.shrink'),
          width: 120,
          render: ({ record: { shrinkageAbsolute } }) => {
            return shrinkageAbsolute ? formatMoney(locale, shrinkageAbsolute) : '-';
          },
        },
        {
          id: 'shrinkage',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 120,
          render: ({ record: { shrinkagePercentage, shrinkagePercentageIsInfinite } }) => {
            if (shrinkagePercentageIsInfinite) return '∞';
            return shrinkagePercentage ? `${formatNumber(locale, shrinkagePercentage, 2)}%` : '-';
          },
        },
        {
          id: 'vendor',
          header: '',
          alignNameVertical: 'middle',
          width: 'auto',
          render: ({ record: { vendor } }) => {
            if (!vendor) return '-';
            return (
              <TextLink to="/vendors/$vendorId" params={{ vendorId: vendor.id }} target="_blank">
                {vendor.name}
              </TextLink>
            );
          },
        },
        {
          id: 'listedFor',
          header: '',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 120,
          render: ({ record: { listedSince } }) => {
            const listingDuration = getListingDuration(listedSince, locale);
            return f('selection_recommendations.listed_for_weeks', {
              count: listingDuration,
            });
          },
        },
        {
          id: 'basketPenetration',
          header: f('selection_recommendations.basket_penetration'),
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 160,
          render: ({ record: { basketPenetrationPercentage } }) => {
            if (!basketPenetrationPercentage) return '-';
            return `${formatNumber(locale, basketPenetrationPercentage, 2)}%`;
          },
        },
        {
          id: 'basketPenetrationPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.basket_penetration_phl2_median'),
          width: 160,
          render: ({ record: { basketPenetrationPercentagePHL2Median } }) => {
            if (!basketPenetrationPercentagePHL2Median) return '-';
            return `${formatNumber(locale, basketPenetrationPercentagePHL2Median, 2)}%`;
          },
        },
        {
          id: 'profitPerUnit',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.profit_per_unit'),
          width: 140,
          render: ({ record: { profitPerUnit } }) => {
            if (!profitPerUnit) return '-';
            return formatMoney(locale, profitPerUnit);
          },
        },
        {
          id: 'profitPerUnitPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.profit_per_unit_phl2_median'),
          width: 140,
          render: ({ record: { profitPerUnitPHL2Median } }) => {
            if (!profitPerUnitPHL2Median) return '-';
            return formatMoney(locale, profitPerUnitPHL2Median);
          },
        },
        {
          id: 'revenue',
          header: f('selection_recommendations.revenue'),
          width: 150,
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          render: ({ record: { totalRevenue } }) => formatMoney(locale, totalRevenue),
        },
      ]}
      parentRow={parent}
    />
  );
};
